import React, {
  forwardRef,
  useEffect,
  useRef,
  useState,
  WheelEvent,
} from "react";
import Scrollbar from "react-scrollbars-custom";
import styled from "styled-components";
import { Track } from "./style/track";
import { Thumb } from "./style/thumb";
import { wheelConvert } from "Util";

const HiddenScrollbar = styled.div`
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

interface CustomScrollContainerProps {
  children: React.ReactNode;
  width: string;
  height: string;
  className?: string;
  maxHeight?: string;
  margin?: string;
  isHorizontalScroll?: boolean;
  onWheel?: (event: React.WheelEvent<HTMLDivElement>) => void;
}

export const CustomScrollContainer = forwardRef<
  Scrollbar,
  CustomScrollContainerProps
>(
  (
    {
      children,
      width,
      height,
      className,
      maxHeight,
      margin,
      isHorizontalScroll,
      onWheel,
    },
    scrollbarRef
  ) => {
    const hoverTimerId = useRef<NodeJS.Timeout>();
    const contentRef = useRef<HTMLDivElement | null>(null);
    const internalRef = useRef<Scrollbar | null>(null);
    const effectiveRef =
      (scrollbarRef as React.RefObject<Scrollbar>) || internalRef;

    const [contentHeight, setContentHeight] = useState(height);
    const [isHovered, setIsHovered] = useState(false);

    const onMouseEnter = () => {
      if (hoverTimerId.current) clearTimeout(hoverTimerId.current);
      setIsHovered(true);
    };
    const onMouseLeave = () => {
      if (hoverTimerId.current) clearTimeout(hoverTimerId.current);
      hoverTimerId.current = setTimeout(() => {
        setIsHovered(false);
      }, 1000);
    };

    const handleWheel = (e: WheelEvent<HTMLDivElement>) => {
      onWheel?.(e);
      if (isHorizontalScroll) {
        wheelConvert(e, effectiveRef);
      }
    };

    useEffect(() => {
      if (contentRef.current && height === "max-content") {
        const contentRect = contentRef.current.getBoundingClientRect();
        setContentHeight(`${contentRect.height}px`);
      }
    }, [height, contentRef.current?.getBoundingClientRect().height]);

    return (
      <Scrollbar
        ref={effectiveRef as React.RefObject<Scrollbar & HTMLDivElement>}
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onWheel={handleWheel}
        style={{
          width,
          maxHeight,
          height: contentHeight,
          position: "relative",
          margin: margin,
          overflow: "auto",
        }}
        trackXProps={{
          renderer: (props) => {
            const { elementRef, ...restProps } = props;
            return <Track {...restProps} ref={elementRef} $xAxis />;
          },
        }}
        trackYProps={{
          renderer: (props) => {
            const { elementRef, ...restProps } = props;
            return <Track {...restProps} ref={elementRef} $yAxis />;
          },
        }}
        thumbXProps={{
          renderer: (props) => {
            const { elementRef, ...restProps } = props;
            return (
              <Thumb
                {...restProps}
                ref={elementRef}
                style={props.style}
                $visible={isHovered}
                $xAxis
              />
            );
          },
        }}
        thumbYProps={{
          renderer: (props) => {
            const { elementRef, ...restProps } = props;
            return (
              <Thumb
                {...restProps}
                ref={elementRef}
                style={props.style}
                $visible={isHovered}
                $yAxis
              />
            );
          },
        }}
        wrapperProps={{
          renderer: (props) => {
            const { elementRef, ...restProps } = props;
            return (
              <div
                {...restProps}
                ref={elementRef}
                style={{ overflow: "hidden" }}
              />
            );
          },
        }}
        contentProps={{
          renderer: (props) => {
            const { elementRef, ...restProps } = props;
            return (
              <div
                {...restProps}
                ref={(el) => {
                  elementRef && elementRef(el);
                  contentRef.current = el;
                }}
                style={{
                  ...props.style,
                  height: height === "max-content" ? height : "100%",
                }}
              >
                {children}
              </div>
            );
          },
        }}
        scrollerProps={{
          renderer: (props) => {
            const { elementRef, ...restProps } = props;
            return (
              <HiddenScrollbar
                {...restProps}
                ref={elementRef}
                style={{
                  ...props.style,
                  marginRight: 0,
                  marginBottom: 0,
                }}
              />
            );
          },
        }}
      />
    );
  }
);
